import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCarousel from "@/components/landing/ProductCarousel";
import ExitIntentModal from "@/components/landing/ExitIntentModal";
import TabelaRecursos from "@/components/landing/TabelaRecursos";
import WhatsAppButton from "@/components/WhatsAppButton";
import { track } from "@/lib/analytics";

const WHATSAPP_LANDING_HREF = `https://wa.me/message/7G7QKQ4PM56KM1?text=${encodeURIComponent(
  "Estou navegando o site do Tuto e quero conhecer mais sobre o sistema."
)}`;
import {
  Hero, StatsBand, HowItWorks, Features, BeforeAfter,
  Integrations, Testimonials, Faq, Pricing, FinalCta,
} from "@/components/landing/sections";

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <ExitIntentModal />
      <WhatsAppButton href={WHATSAPP_LANDING_HREF} />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#D1DEEE]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between md:justify-center md:gap-10">
          <div className="flex items-center">
            <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="h-8 w-auto" />
          </div>

          <nav className="hidden md:flex items-center gap-10">
            <a href="#funcionalidades" className="text-[#335480] hover:text-[#003882] transition-colors text-base font-medium">Funcionalidades</a>
            <a href="#planos" className="text-[#335480] hover:text-[#003882] transition-colors text-base font-medium">Planos</a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-[#003882] hover:text-[#00285e] font-medium text-lg transition-colors">Já tenho conta</Link>
            <Button data-testid="header-signup-btn" onClick={() => { track("cta_click", { location: "header" }); track("signup_start", { location: "header" }); navigate("/registro"); }} className="bg-[#003882] hover:bg-[#00285e] text-white font-medium text-lg h-12 px-6">
              Começar agora
            </Button>
          </div>

          <button className="md:hidden p-2 text-[#335480] hover:text-[#003882]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Abrir menu">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#D1DEEE] px-6 py-4 flex flex-col gap-4">
            <a href="#funcionalidades" className="text-[#335480] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Funcionalidades</a>
            <a href="#planos" className="text-[#335480] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Planos</a>
            <hr className="border-[#D1DEEE]" />
            <Link to="/login" className="text-[#003882] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Já tenho conta</Link>
            <Button onClick={() => { setMobileMenuOpen(false); navigate("/registro"); }} className="bg-[#003882] hover:bg-[#00285e] text-white font-medium w-full">
              Começar agora
            </Button>
          </div>
        )}
      </header>

      {/* Seções (ordem do PRD) */}
      <Hero />
      <StatsBand />
      <HowItWorks />
      <ProductCarousel />
      <Features />
      <BeforeAfter />
      <Integrations />
      <Testimonials />
      <Faq />
      <Pricing />
      <TabelaRecursos />
      <FinalCta />

      {/* Footer */}
      <footer className="bg-[#001533] text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">
            <div>
              <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="h-8 w-auto brightness-0 invert mb-3" />
              <p className="text-[#6685AD] text-sm max-w-xs leading-relaxed">
                Plataforma de gestão jurídica para advogados brasileiros, com cobertura nacional de todos os tribunais.
              </p>
              {/* Selos movidos do social proof para o rodapé */}
              <div className="flex items-center gap-4 mt-5">
                <img src="/badge-founders-prime.png" alt="Founders Club Prime — Membro Prime" className="h-12 w-auto object-contain" />
                <img src="/badge-startup-verificada.png" alt="Founders Club — Startup Verificada" className="h-12 w-auto object-contain" />
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <p className="text-white font-semibold text-sm">Legal</p>
              <Link to="/termos-de-uso" className="text-blue-300 hover:text-white text-sm transition-colors">Termos de Uso</Link>
              <Link to="/politica-de-privacidade" className="text-blue-300 hover:text-white text-sm transition-colors">Política de Privacidade</Link>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-[#6685AD] text-sm">
              © 2026{" "}
              <a href="https://www.kosmosai.com.br" target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-blue-300 transition-colors">
                TUTO — Powered by Kosmos
              </a>
            </p>
            <p className="text-[#6685AD] text-xs">
              CNPJ 08.277.372/0001-71 — Keep Soluções Tecnológicas Ltda. · v{process.env.REACT_APP_VERSION || "0.1.0"}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
